import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AvailabilityMonthGrid } from '@apps/web/components/caregivers/caregiver_availability_month_grid';
import {
    buildClientScheduleDayGroups,
    countClientScheduleFill,
} from '@apps/web/components/clients/client_schedule_model';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    addMonthsToDateKey,
    endOfMonthDateKey,
    formatMonthKey,
    midnightUtcIso,
    startOfMonthDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { useShifts } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type ClientScheduleCalendarProps = {
    readonly organizationId: string;
    readonly clientId: string;
    readonly timezone: string;
    readonly onShiftClick: (shiftId: string) => void;
};

/**
 * Month calendar of a client's shifts: who is covering each visit, with
 * unfilled shifts highlighted as open. Read-only view over the existing
 * shifts endpoint (client_ids filter).
 */
export function ClientScheduleCalendar({
    organizationId,
    clientId,
    timezone,
    onShiftClick,
}: ClientScheduleCalendarProps) {
    const { formatName } = useFormatPersonName();
    const todayKey = useMemo(() => toDateKey(new Date(), timezone), [timezone]);
    // Null until the user navigates; the visible month then follows today.
    const [anchorOverrideKey, setAnchorOverrideKey] = useState<string | null>(
        null,
    );
    const anchorDateKey = anchorOverrideKey ?? todayKey;
    const monthStartKey = startOfMonthDateKey(anchorDateKey);
    const monthEndKey = endOfMonthDateKey(monthStartKey);

    const { data, error, isLoading } = useShifts(
        organizationId,
        { reason: 'client-schedule-calendar' },
        {
            client_ids: [clientId],
            shift_start_date: midnightUtcIso(monthStartKey, timezone),
            shift_end_date: midnightUtcIso(monthEndKey, timezone, {
                isEnd: true,
            }),
            order: 'asc',
            limit: 1000,
        },
        { keepPreviousData: false },
    );

    const dayGroups = useMemo(
        () =>
            buildClientScheduleDayGroups({
                shifts: data?.shifts ?? [],
                timezone,
                formatName,
            }),
        [data?.shifts, timezone, formatName],
    );
    const fill = useMemo(() => countClientScheduleFill(dayGroups), [dayGroups]);
    const hasShifts = fill.filled + fill.open > 0;

    return (
        <div className="flex min-h-full flex-col gap-2">
            <div className="flex items-center gap-0.5">
                <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label="Previous month"
                    onClick={() =>
                        setAnchorOverrideKey(
                            addMonthsToDateKey(monthStartKey, -1),
                        )
                    }
                >
                    <ChevronLeft className="size-3.5" />
                </Button>
                <span className="text-xs font-medium">
                    {formatMonthKey(monthStartKey, timezone)}
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label="Next month"
                    onClick={() =>
                        setAnchorOverrideKey(
                            addMonthsToDateKey(monthStartKey, 1),
                        )
                    }
                >
                    <ChevronRight className="size-3.5" />
                </Button>
                <span
                    data-testid="client-schedule-fill"
                    className={cn(
                        'text-muted-foreground ml-auto text-xs tabular-nums',
                        !hasShifts && 'hidden',
                    )}
                >
                    {`${fill.filled} filled · ${fill.open} open`}
                </span>
                {isLoading && <Spinner className="size-4" />}
            </div>
            {error ? (
                <div
                    className="text-destructive flex min-h-40 items-center justify-center text-sm"
                    role="alert"
                >
                    Failed to load client schedule.
                </div>
            ) : (
                <AvailabilityMonthGrid
                    monthStartKey={monthStartKey}
                    todayKey={todayKey}
                    timezone={timezone}
                    dayGroups={dayGroups}
                    onShiftClick={onShiftClick}
                    hideLegend={true}
                    fill={true}
                />
            )}
            <div className="text-muted-foreground flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                    <span
                        className="bg-secondary-foreground/60 size-2 rounded-full"
                        aria-hidden={true}
                    />
                    Filled
                </span>
                <span className="flex items-center gap-1">
                    <span
                        className="bg-destructive size-2 rounded-full"
                        aria-hidden={true}
                    />
                    Open
                </span>
            </div>
        </div>
    );
}
